import axios from 'axios'
import i18next from 'i18next'
import config from 'config'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import history from 'srcReact/helpers/history'
import { delay, getCurrentLanguageFromCookie, redirectInternalError } from 'srcReact/helpers/utils'
import { userService } from 'srcReact/services/userService'
import { checkNested } from './validator'
import { isUserAuthorized } from './userHelper'
import { getCurrentApiDomain, getCurrentOrgId } from './organizationHelper'

export const doRequestPlainGet = (url) => {
  return axios
    .get(url)
    .then((response) => response.data)
    .catch(handleAxiosError)
}

export const doRequestAxiosAllWithDiffParams = (requestUrls, apiTriggerCount = appConstantSettings.RETRY_COUNT) => {
  let result = null

  result = axios.all(requestUrls)

  return result
    .then(
      axios.spread((...responses) => {
        return responses.map((response) =>
          handleAxiosResponse(response, requestOptions, null, null, apiTriggerCount, 'axios')
        )
      })
    )
    .catch(handleAxiosError)
}

export const doRequestAxiosAll = (urls, requestOptions, apiTriggerCount = appConstantSettings.RETRY_COUNT) => {
  let result = null
  const orgId = getCurrentOrgId()
  const language = getCurrentLanguageFromCookie()
  if (isUserAuthorized() && orgId) {
    requestOptions = { ...requestOptions, params: { oid: orgId, language: language, ...requestOptions.params } }
  }
  const updateUrls = urls.map((url) => {
    let updatedUrl = ''
    const domain = getCurrentApiDomain()
    const domainUrl = domain && domain.length > 0 ? `https://${domain}/api` : config.apiUrl
    let apiUrl = `${domainUrl}${url}`
    switch (requestOptions.method.toLowerCase()) {
      case 'get':
        updatedUrl = axiosGet(apiUrl, requestOptions)
        break
      case 'post':
        updatedUrl = axiosPost(apiUrl, requestOptions)
        break
      case 'put':
        updatedUrl = axiosPut(apiUrl, requestOptions)
        break
      case 'delete':
        updatedUrl = axiosDelete(apiUrl, requestOptions)
        break
      default:
        updatedUrl = ''
    }
    return updatedUrl
  })

  result = axios.all(updateUrls)

  return result
    .then(
      axios.spread((...responses) => {
        return responses.map((response) =>
          handleAxiosResponse(response, requestOptions, null, null, apiTriggerCount, 'axios')
        )
      })
    )
    .catch(handleAxiosError)
}

export const doRequestAxios = (
  url,
  requestOptions,
  apiTriggerCount = appConstantSettings.RETRY_COUNT,
  apiSubDomainUrl = '',
  passLanguage = true
) => {
  let result = null
  const orgId = getCurrentOrgId()
  const language = getCurrentLanguageFromCookie()
  if (isUserAuthorized() && orgId) {
    if (requestOptions.params instanceof FormData) {
      requestOptions.params.append('oid', orgId)
    } else {
      requestOptions = {
        ...requestOptions,
        params: { oid: orgId, ...(passLanguage ? { language: language } : {}), ...requestOptions.params }
      }
    }
  }
  let domainUrl = ''
  if (url.includes('/organization/search')) {
    domainUrl = config.apiUrl
  } else {
    if (apiSubDomainUrl && apiSubDomainUrl.length > 0) {
      domainUrl = `https://${apiSubDomainUrl}/api`
    } else {
      const domain = getCurrentApiDomain()
      domainUrl = domain && domain.length > 0 ? `https://${domain}/api` : config.apiUrl
    }
  }
  let apiUrl = `${domainUrl}${url}`
  switch (requestOptions.method.toLowerCase()) {
    case 'get':
      result = axiosGet(apiUrl, requestOptions)
      break
    case 'post':
      result = axiosPost(apiUrl, requestOptions)
      break
    case 'put':
      result = axiosPut(apiUrl, requestOptions)
      break
    case 'delete':
      result = axiosDelete(apiUrl, requestOptions)
      break
    case 'patch':
      result = axiosPatch(apiUrl, requestOptions)
      break
    default:
      result = null
  }

  return result
    .then((response) => handleAxiosResponse(response, requestOptions, null, null, apiTriggerCount, 'axios'))
    .catch(handleAxiosError)
}

const axiosGet = (url, requestOptions) => {
  return axios.get(url, requestOptions)
}

const axiosPost = (url, requestOptions) => {
  return axios.post(url, requestOptions.data || requestOptions.params, requestOptions)
}

const axiosPut = (url, requestOptions) => {
  return axios.put(url, requestOptions.data || requestOptions.params, requestOptions)
}

const axiosDelete = (url, requestOptions) => {
  return axios.delete(url, requestOptions)
}

const axiosPatch = (url, requestOptions) => {
  return axios.patch(url, requestOptions.params, requestOptions)
}

export function handleAxiosResponse(
  response,
  requestOptions,
  redirect = true,
  doNothing = false,
  apiTriggerCount,
  type
) {
  if (response) {
    let data = {}
    if (response.data && response.data.status) {
      data = response.data
    } else {
      data = {
        data: response.data,
        status: response.status,
        success: response.status === 200
      }
    }
    return processResponse(data, response.url, requestOptions, redirect, doNothing, apiTriggerCount, type)
  }
  return null
}

export function handleAxiosError(error) {
  if (axios.isCancel(error)) {
    return Promise.reject({ cancelError: error, message: 'cancelTokenError' })
  }
  console.error('error::', error.response)
  if (error?.response?.data) {
    if (checkNested(error, 'response', 'data', 'message')) {
      //special case for get profile error. Need to optimize this once backend is consistent
      if (checkNested(error, 'response', 'config') && error.response.config.url.includes('/user/profile/get')) {
        if (
          checkNested(error, 'response', 'data', 'error', 'user_id') &&
          error.response.data.error.status === 'deleted'
        ) {
          return Promise.reject('deleted user')
        } else {
          return Promise.reject(error.response.data.message)
        }
      } else if (error.response.status === 422) {
        return Promise.reject({
          errorCode: 422,
          errorMessage: error.response.data.message
        })
      } else {
        if (error.response.config.url.includes('/workplace/invitation/bulk')) {
          return Promise.reject({ ...error.response.data.error, message: error.response.data.message })
        }
        if (error.response.config.url.includes('/v2/mixr/information')) {
          return Promise.reject({ ...error.response.data.error, message: error.response.data.message })
        }
        return Promise.reject(error.response.data.message)
      }
    } else if (error?.response?.status == 500) {
      console.error(error.response.data)
      return Promise.reject(appConstantSettings.HTTP500)
    }
  } else if (checkNested(error, 'response', 'statusText')) {
    return Promise.reject(error.response.statusText)
  } else {
    return Promise.reject(i18next.t('errorPage.clientTitle'))
  }

  // if (error.response) {
  //     // The request was made and the server responded with a status code
  //     // that falls out of the range of 2xx
  //     // return Promise.reject(error.response.data.message)
  //     const status = error.response.status
  //     if (status === 400 || status === 401 || status === 404 || status === 403) {
  //         return Promise.reject(error.response.data.message)
  //     }
  //     else {
  //         //e.g 500 error
  //         return Promise.reject(i18next.t('errorPage.clientTitle'))
  //     }

  // } else if (error.request) {
  //     // The request was made but no response was received
  //     // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
  //     // http.ClientRequest in node.js
  //     return Promise.reject(i18next.t('errorPage.clientTitle'))
  // } else {
  //     // Something happened in setting up the request that triggered an Error
  //     return Promise.reject(i18next.t('errorPage.clientTitle'))
  // }
}

/////////////////////////

const processResponse = (data, url, requestOptions, redirect, doNothing, apiTriggerCount) => {
  if (!data.success || data.success == false) {
    if (data.status == 503) {
      if (apiTriggerCount === 1) {
        return Promise.reject({ message: i18next.t('global.serverUnavailableError') })
      } else {
        return delay(appConstantSettings.RETRY_DURATION).then(() => doRequestAxios(requestOptions, apiTriggerCount - 1))
      }
    } else if (data.status == 500) {
      redirectInternalError(history, data)
    } else if (!doNothing && data.status === 405) {
      userService.logout(true)
    } else if (redirect && data.status === 401) {
      userService.logout(true)
    } else {
      const message = (data && data.message) || response.statusText
      if (data && data.error && data.error.hasOwnProperty('can_retry')) {
        return Promise.reject({ error: data.error, message })
      }
      return Promise.reject({ message, error: message, data: data.error })
    }
  } else {
    return data.data
  }
}
